import { useEffect, useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountDetailSchema,
  AssetSchema,
  ClientSchema,
  EmployeeSchema,
  JournalDetailSchema,
  ProjectSchema,
  SupplierSchema,
  type AccountDto,
  type AssetDto,
  type ClientDto,
  type EmployeeDto,
  type JournalDetailDto,
  type ProjectDto,
  type SupplierDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ActiveClientPicker, ActiveProjectPicker } from '../crm/common';
import { refreshAfterPosting } from '../finance/cache';
import { RecordFailure } from '../finance/detail';
import { businessToday, requirementText } from '../finance/line-editor';
import {
  AccountPicker,
  EmployeePicker,
  MasterSelect,
  SupplierPicker,
  useActiveMasters,
} from '../finance/lookups';
import { isAmount, sum, toFils } from '../finance/money';
import { AssetPicker } from '../procurement/purchasing';

const JOURNALS = '/api/v1/ledger/journals';

interface Line {
  key: number;
  account: AccountDto | null;
  debit: string;
  credit: string;
  description: string;
  costCenterId: string;
  locationId: string;
  employee: EmployeeDto | null;
  supplier: SupplierDto | null;
  client: ClientDto | null;
  project: ProjectDto | null;
  asset: AssetDto | null;
}

const emptyLine = (key: number): Line => ({
  key,
  account: null,
  debit: '',
  credit: '',
  description: '',
  costCenterId: '',
  locationId: '',
  employee: null,
  supplier: null,
  client: null,
  project: null,
  asset: null,
});

type Kind = 'standard' | 'adjustment' | 'controlled_adjustment';

/**
 * Prepares or amends a manual journal draft (`/journals/new`, `/journals/:id/edit`;
 * FRD-FIN-040, FIN-043). Each line names an account, one side, and every dimension that
 * account's COA flags require, from the real masters (FRD-FIN-020); totals show whether it
 * balances. The API decides everything at posting (FRD-FIN-041): a draft may be saved unbalanced
 * while it is being prepared. A controlled adjustment (FRD-FIN-054) needs its separate permission
 * and a reason; an adjustment names the posted journal it corrects (FRD-FIN-044).
 */
export function JournalEditorPage() {
  const { id } = useParams();
  const existing = useQuery({
    queryKey: ['journal', id ?? 'new'],
    enabled: id !== undefined,
    queryFn: () => getData(`${JOURNALS}/${id ?? ''}`, JournalDetailSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to={id === undefined ? '/journals' : `/journals/${id}`}
      startIcon={<ArrowBackIcon />}
    >
      {id === undefined ? 'All journals' : 'Back to the journal'}
    </Button>
  );
  if (id !== undefined && existing.isPending) return <LoadingState label="Loading the journal" />;
  if (id !== undefined && existing.isError) {
    return <RecordFailure title="Journal" noun="journal" error={existing.error} back={back} />;
  }
  return <Editor existing={existing.data ?? null} back={back} />;
}

function Editor({ existing, back }: { existing: JournalDetailDto | null; back: ReactNode }) {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = (capability: string) => hasCapability(session.data, capability);
  const canControlled = can('ledger.controlled_adjustment:create');
  const [kind, setKind] = useState<Kind>(
    (existing?.journal.journalKind as Kind | undefined) ?? 'standard',
  );
  const [postingDate, setPostingDate] = useState(
    existing?.journal.postingDate ?? businessToday(session.data?.businessTimezone),
  );
  const [reference, setReference] = useState(existing?.journal.reference ?? '');
  const [description, setDescription] = useState(existing?.journal.description ?? '');
  const [reason, setReason] = useState(existing?.journal.adjustmentReason ?? '');
  const [originalId, setOriginalId] = useState(existing?.original?.id ?? '');
  const [lines, setLines] = useState<Line[]>(() =>
    existing === null ? [emptyLine(1), emptyLine(2)] : [],
  );
  const [loading, setLoading] = useState(existing !== null);
  const idempotencyKey = useRef(newIdempotencyKey());

  // An existing draft's lines are rebuilt from the masters, so every picker shows the record.
  useEffect(() => {
    if (existing === null) return;
    const run = { cancelled: false };
    void (async () => {
      const rebuilt: Line[] = [];
      for (const line of existing.lines) {
        const maybe = async <T,>(capability: string, fetch: () => Promise<T>) =>
          can(capability) ? fetch().catch(() => null) : null;
        rebuilt.push({
          key: line.lineNo,
          account: await getData(`/api/v1/ledger/accounts/${line.accountId}`, AccountDetailSchema)
            .then((d) => d.account)
            .catch(() => null),
          debit: line.debit === '0.00' ? '' : line.debit,
          credit: line.credit === '0.00' ? '' : line.credit,
          description: line.description ?? '',
          costCenterId: line.costCenterId ?? '',
          locationId: line.locationId ?? '',
          employee:
            line.employeeId === null
              ? null
              : await maybe('hr.employee:view', () =>
                  getData(`/api/v1/hr/employees/${line.employeeId ?? ''}`, EmployeeSchema),
                ),
          supplier:
            line.supplierId === null
              ? null
              : await maybe('procurement.supplier:view', () =>
                  getData(`/api/v1/procurement/suppliers/${line.supplierId ?? ''}`, SupplierSchema),
                ),
          client:
            line.clientId === null
              ? null
              : await maybe('crm.client:view', () =>
                  getData(`/api/v1/crm/clients/${line.clientId ?? ''}`, ClientSchema),
                ),
          project:
            line.projectId === null
              ? null
              : await maybe('projects.project:view', () =>
                  getData(`/api/v1/projects/${line.projectId ?? ''}`, ProjectSchema),
                ),
          asset:
            line.assetId === null
              ? null
              : await maybe('assets.asset:view', () =>
                  getData(`/api/v1/assets/assets/${line.assetId ?? ''}`, AssetSchema),
                ),
        });
      }
      if (!run.cancelled) {
        setLines(rebuilt);
        setLoading(false);
      }
    })();
    return () => {
      run.cancelled = true;
    };
    // The draft is read once, when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  const body = () => ({
    journalKind: kind,
    postingDate,
    ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
    ...(description.trim() !== '' ? { description: description.trim() } : {}),
    ...(reason.trim() !== '' ? { adjustmentReason: reason.trim() } : {}),
    ...(kind === 'adjustment' && originalId.trim() !== ''
      ? { originalJournalId: originalId.trim() }
      : {}),
    lines: lines.map((line) => ({
      accountId: line.account?.id ?? '',
      debit: line.debit.trim(),
      credit: line.credit.trim(),
      ...(line.description.trim() !== '' ? { description: line.description.trim() } : {}),
      ...(line.costCenterId !== '' ? { costCenterId: line.costCenterId } : {}),
      ...(line.locationId !== '' ? { locationId: line.locationId } : {}),
      ...(line.employee !== null ? { employeeId: line.employee.id } : {}),
      ...(line.supplier !== null ? { supplierId: line.supplier.id } : {}),
      ...(line.client !== null ? { clientId: line.client.id } : {}),
      ...(line.project !== null ? { projectId: line.project.id } : {}),
      ...(line.asset !== null ? { assetId: line.asset.id } : {}),
    })),
  });
  const save = useMutation({
    mutationFn: () =>
      existing === null
        ? postJson(JOURNALS, body(), JournalDetailSchema, session.data?.csrfToken ?? '', {
            idempotencyKey: idempotencyKey.current,
          })
        : patchJson(
            `${JOURNALS}/${existing.journal.id}`,
            body(),
            JournalDetailSchema,
            session.data?.csrfToken ?? '',
            existing.journal.version,
          ),
    onSuccess: async (saved) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/journals/${saved.journal.id}`);
    },
  });

  const problems = lines.flatMap((line, index) => {
    const n = String(index + 1);
    const out: string[] = [];
    if (line.account === null) out.push(`Line ${n}: choose an account.`);
    const debit = line.debit.trim();
    const credit = line.credit.trim();
    if ((debit === '') === (credit === '')) out.push(`Line ${n}: enter a debit or a credit.`);
    else if (!isAmount(debit === '' ? credit : debit))
      out.push(`Line ${n}: the amount is not valid.`);
    const needs = line.account?.requires;
    if (needs?.costCenter === true && line.costCenterId === '')
      out.push(`Line ${n}: choose a cost centre.`);
    if (needs?.employee === true && line.employee === null)
      out.push(`Line ${n}: choose an employee.`);
    if (needs?.supplier === true && line.supplier === null)
      out.push(`Line ${n}: choose a supplier.`);
    if (needs?.client === true && line.client === null) out.push(`Line ${n}: choose a client.`);
    if (needs?.project === true && line.project === null) out.push(`Line ${n}: choose a project.`);
    if (needs?.asset === true && line.asset === null) out.push(`Line ${n}: choose an asset.`);
    if (kind !== 'controlled_adjustment' && line.account?.isSystemControlled === true) {
      out.push(
        `Line ${n}: ${line.account.code} is a control account; only a controlled adjustment may post to it.`,
      );
    }
    return out;
  });
  if (kind === 'controlled_adjustment' && reason.trim() === '') {
    problems.push('A controlled adjustment records its reason.');
  }
  if (kind === 'adjustment' && originalId.trim() === '') {
    problems.push('An adjustment names the posted journal it corrects.');
  }
  if (lines.length < 2) problems.push('A journal has at least two lines.');
  const totalDebit = sum(lines.map((l) => l.debit));
  const totalCredit = sum(lines.map((l) => l.credit));
  const balanced = toFils(totalDebit) === toFils(totalCredit) && toFils(totalDebit) > 0n;
  const update = (key: number, patch: Partial<Line>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };
  const nextKey = lines.reduce((max, line) => Math.max(max, line.key), 0) + 1;

  if (loading) return <LoadingState label="Loading the journal lines" />;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={existing === null ? 'New journal' : `Edit ${existing.journal.documentNo}`}
        description="A draft does not affect balances until it is posted."
        actions={back}
      />
      <Section title="Header">
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Kind"
              value={kind}
              disabled={existing !== null}
              sx={{ minWidth: 220 }}
              onChange={(e) => {
                setKind(e.target.value as Kind);
              }}
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="adjustment">Adjustment of a posted journal</MenuItem>
              {canControlled && (
                <MenuItem value="controlled_adjustment">Controlled adjustment</MenuItem>
              )}
            </TextField>
            <TextField
              type="date"
              label="Posting date"
              required
              value={postingDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setPostingDate(e.target.value);
              }}
            />
            <TextField
              label="Reference"
              value={reference}
              onChange={(e) => {
                setReference(e.target.value);
              }}
            />
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
          {kind === 'adjustment' && (
            <TextField
              label="Journal it adjusts (id)"
              required
              value={originalId}
              disabled={existing !== null}
              helperText="Open the posted journal and use Adjust, or enter its id"
              onChange={(e) => {
                setOriginalId(e.target.value);
              }}
            />
          )}
          {kind === 'controlled_adjustment' && (
            <TextField
              label="Reason for the controlled adjustment"
              required
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          )}
        </Stack>
      </Section>
      <Section title="Lines">
        <Stack spacing={2} sx={{ p: 2 }}>
          {lines.map((line, index) => (
            <JournalLineEditor
              key={line.key}
              n={index + 1}
              line={line}
              removable={lines.length > 2}
              onChange={(patch) => {
                update(line.key, patch);
              }}
              onRemove={() => {
                setLines((current) => current.filter((l) => l.key !== line.key));
              }}
            />
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setLines((current) => [...current, emptyLine(nextKey)]);
              }}
            >
              Add line
            </Button>
          </Box>
          <Typography variant="subtitle2" aria-live="polite">
            Total debit {totalDebit} · total credit {totalCredit} ·{' '}
            {balanced ? 'balanced' : 'not balanced yet'}
          </Typography>
        </Stack>
      </Section>
      {problems.length > 0 && (
        <Alert severity="info" variant="outlined">
          <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      {save.isError && <Alert severity="error">{save.error.message}</Alert>}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={problems.length > 0 || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {existing === null ? 'Save draft' : 'Save changes'}
        </Button>
      </Stack>
    </Stack>
  );
}

function JournalLineEditor({
  n,
  line,
  removable,
  onChange,
  onRemove,
}: {
  n: number;
  line: Line;
  removable: boolean;
  onChange: (patch: Partial<Line>) => void;
  onRemove: () => void;
}) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const costCentres = useActiveMasters(
    '/api/v1/organization/cost-centers',
    can('organization.cost_center:view'),
  );
  const locations = useActiveMasters(
    '/api/v1/organization/locations',
    can('organization.location:view'),
  );
  const needs = line.account?.requires;
  const denied = (what: string, capability: string) =>
    can(capability) ? undefined : `Needs permission to view ${what}`;
  return (
    <Box
      role="group"
      aria-label={`Line ${String(n)}`}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography variant="subtitle2">Line {n}</Typography>
        {removable && (
          <IconButton size="small" aria-label={`Remove line ${String(n)}`} onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <Stack spacing={2}>
        <AccountPicker
          value={line.account}
          onChange={(account) => {
            onChange({ account });
          }}
          helperText={requirementText(line.account)}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Debit"
            value={line.debit}
            onChange={(e) => {
              onChange({ debit: e.target.value });
            }}
          />
          <TextField
            size="small"
            label="Credit"
            value={line.credit}
            onChange={(e) => {
              onChange({ credit: e.target.value });
            }}
          />
          <TextField
            size="small"
            label="Line description"
            value={line.description}
            sx={{ flexGrow: 1 }}
            onChange={(e) => {
              onChange({ description: e.target.value });
            }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <MasterSelect
            label="Cost centre"
            value={line.costCenterId}
            required={needs?.costCenter === true}
            error={needs?.costCenter === true && line.costCenterId === ''}
            disabled={!can('organization.cost_center:view')}
            helperText={denied('cost centres', 'organization.cost_center:view')}
            options={costCentres.data?.items ?? []}
            onChange={(value) => {
              onChange({ costCenterId: value });
            }}
          />
          <MasterSelect
            label="Location"
            value={line.locationId}
            disabled={!can('organization.location:view')}
            helperText={denied('locations', 'organization.location:view')}
            options={locations.data?.items ?? []}
            onChange={(value) => {
              onChange({ locationId: value });
            }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <EmployeePicker
              value={line.employee}
              required={needs?.employee === true}
              error={needs?.employee === true && line.employee === null}
              disabled={!can('hr.employee:view')}
              helperText={denied('employees', 'hr.employee:view')}
              onChange={(employee) => {
                onChange({ employee });
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <SupplierPicker
              value={line.supplier}
              required={needs?.supplier === true}
              error={needs?.supplier === true && line.supplier === null}
              disabled={!can('procurement.supplier:view')}
              helperText={denied('suppliers', 'procurement.supplier:view')}
              onChange={(supplier) => {
                onChange({ supplier });
              }}
            />
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <ActiveClientPicker
              label="Client"
              value={line.client}
              required={needs?.client === true}
              error={needs?.client === true && line.client === null}
              disabled={!can('crm.client:view')}
              helperText={denied('clients', 'crm.client:view')}
              onChange={(client) => {
                onChange({ client });
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <ActiveProjectPicker
              label="Project"
              value={line.project}
              required={needs?.project === true}
              error={needs?.project === true && line.project === null}
              disabled={!can('projects.project:view')}
              helperText={denied('projects', 'projects.project:view')}
              onChange={(project) => {
                onChange({ project });
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <AssetPicker
              value={line.asset}
              required={needs?.asset === true}
              error={needs?.asset === true && line.asset === null}
              disabled={!can('assets.asset:view')}
              helperText={denied('assets', 'assets.asset:view')}
              onChange={(asset) => {
                onChange({ asset });
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
